/**
 * Outgoing mail server: the emails Lumen sends to patrons (portal account
 * activation, forgotten password). The password is write-only.
 */

import { useId, useState, type FormEvent } from "react";
import { AlertCircle, CheckCircle2, Info, Loader2, Mail } from "lucide-react";
import { useMailSettings, useSaveMailSettings, useSendTestMail } from "@/api/hooks";
import type { MailSecurity, MailSettings } from "@/api/types";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const SECURITIES: Array<{ value: MailSecurity; label: string; port: number }> = [
  { value: "starttls", label: "STARTTLS (port 587, recommandé)", port: 587 },
  { value: "tls", label: "TLS implicite (port 465)", port: 465 },
  { value: "none", label: "Aucun chiffrement (relais du réseau interne)", port: 25 },
];

const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500";
const labelClass = "mb-1.5 block text-sm text-gray-700 dark:text-gray-200";
const hintClass = "mt-1 text-xs text-gray-500 dark:text-gray-400";

const dateFormat = new Intl.DateTimeFormat("fr-FR", { dateStyle: "medium", timeStyle: "short" });

export function MailSettingsPage() {
  const settings = useMailSettings();

  return (
    <div className="max-w-3xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Courriels</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Serveur d&apos;envoi des courriels aux lecteurs : activation du compte portail et mot de
          passe oublié. Les paramètres sont fournis par le service informatique de la collectivité
          ou par votre messagerie.
        </p>
      </header>

      {settings.isLoading ? (
        <div aria-busy="true" aria-label="Chargement des réglages" className="space-y-4">
          <div className="h-24 animate-pulse rounded-2xl bg-gray-100 dark:bg-gray-800" />
          <div className="h-96 animate-pulse rounded-2xl bg-gray-100 dark:bg-gray-800" />
        </div>
      ) : settings.error || !settings.data ? (
        <p
          role="alert"
          className="flex items-center gap-2 rounded-lg bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-300"
        >
          <AlertCircle className="h-4 w-4" aria-hidden />
          Impossible de charger les réglages des courriels.
        </p>
      ) : (
        <>
          <TestPanel settings={settings.data} />
          <SettingsForm settings={settings.data} />
        </>
      )}
    </div>
  );
}

function TestPanel({ settings }: { settings: MailSettings }) {
  const { user } = useAuth();
  const test = useSendTestMail();
  const id = useId();
  const [recipient, setRecipient] = useState(user?.email ?? "");

  const tone =
    settings.last_test_status === "ok"
      ? "ok"
      : settings.last_test_status === "error"
        ? "error"
        : "neutral";

  return (
    <section
      aria-labelledby={`${id}-title`}
      className={cn(
        "space-y-4 rounded-2xl border p-5",
        tone === "ok" &&
          "border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/60 dark:bg-emerald-950/30",
        tone === "error" && "border-red-200 bg-red-50/60 dark:border-red-900/60 dark:bg-red-950/30",
        tone === "neutral" && "border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900"
      )}
    >
      <div className="flex gap-3">
        {tone === "ok" ? (
          <CheckCircle2
            className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400"
            aria-hidden
          />
        ) : tone === "error" ? (
          <AlertCircle
            className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400"
            aria-hidden
          />
        ) : (
          <Mail className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" aria-hidden />
        )}
        <div className="min-w-0">
          <h2 id={`${id}-title`} className="text-sm font-semibold text-gray-900 dark:text-white">
            {settings.origin === "database"
              ? "Serveur défini ici"
              : "Serveur défini par la configuration du serveur (MAILER_DSN)"}
          </h2>
          <p className="mt-0.5 text-sm break-words text-gray-600 dark:text-gray-300">
            {settings.last_test_at
              ? `${dateFormat.format(new Date(settings.last_test_at))} : ${settings.last_test_message ?? ""}`
              : "Envoyez un courriel de test pour vérifier les réglages."}
          </p>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Expéditeur : {settings.effective_from_address} · Liens vers{" "}
            {settings.effective_portal_url}
          </p>
        </div>
      </div>

      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          test.mutate(recipient);
        }}
      >
        <div className="min-w-56 flex-1">
          <label htmlFor={`${id}-recipient`} className={labelClass}>
            Envoyer un courriel de test à
          </label>
          <input
            id={`${id}-recipient`}
            type="email"
            value={recipient}
            onChange={(e) => {
              setRecipient(e.target.value);
            }}
            required
            className={inputClass}
          />
        </div>
        <Button type="submit" variant="outline" disabled={test.isPending}>
          {test.isPending && <Loader2 className="animate-spin" aria-hidden />}
          Envoyer le test
        </Button>
      </form>

      {/* The outcome shows in the card above; this announces it */}
      <p role="status" className="sr-only">
        {test.isPending ? "" : (test.data?.message ?? "")}
      </p>
      {test.error && (
        <p role="alert" className="text-sm text-red-700 dark:text-red-400">
          {test.error.message}
        </p>
      )}
    </section>
  );
}

function SettingsForm({ settings }: { settings: MailSettings }) {
  const save = useSaveMailSettings();
  const id = useId();

  const [host, setHost] = useState(settings.host ?? "");
  const [port, setPort] = useState(String(settings.port));
  const [security, setSecurity] = useState<MailSecurity>(settings.security);
  const [username, setUsername] = useState(settings.username ?? "");
  const [password, setPassword] = useState("");
  const [fromAddress, setFromAddress] = useState(settings.from_address ?? "");
  const [portalUrl, setPortalUrl] = useState(settings.portal_url ?? "");
  const [saved, setSaved] = useState(false);

  // The stored password belongs to the saved account
  const keepsPassword =
    settings.has_password && settings.password_readable && username.trim() === settings.username;

  const edit =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      setSaved(false);
    };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(
      {
        host,
        port: Number(port),
        security,
        username,
        password,
        from_address: fromAddress,
        portal_url: portalUrl,
      },
      {
        onSuccess: () => {
          setPassword("");
          setSaved(true);
        },
      }
    );
  };

  return (
    <form
      onSubmit={submit}
      aria-labelledby={`${id}-title`}
      className="space-y-6 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900"
    >
      <h2 id={`${id}-title`} className="text-base font-semibold text-gray-900 dark:text-white">
        Serveur d&apos;envoi (SMTP)
      </h2>

      <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
        <div>
          <label htmlFor={`${id}-host`} className={labelClass}>
            Serveur
          </label>
          <input
            id={`${id}-host`}
            value={host}
            onChange={(e) => {
              edit(setHost)(e.target.value);
            }}
            maxLength={255}
            spellCheck={false}
            autoComplete="off"
            placeholder="smtp.example.fr"
            aria-describedby={`${id}-host-hint`}
            className={inputClass}
          />
          <p id={`${id}-host-hint`} className={hintClass}>
            Laissez vide pour utiliser la configuration du serveur (variable MAILER_DSN).
          </p>
        </div>
        <div>
          <label htmlFor={`${id}-port`} className={labelClass}>
            Port
          </label>
          <input
            id={`${id}-port`}
            type="number"
            inputMode="numeric"
            min={1}
            max={65535}
            value={port}
            onChange={(e) => {
              edit(setPort)(e.target.value);
            }}
            required
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label htmlFor={`${id}-security`} className={labelClass}>
          Chiffrement
        </label>
        <select
          id={`${id}-security`}
          value={security}
          onChange={(e) => {
            const value = e.target.value as MailSecurity;
            edit(setSecurity)(value);
            // Suggest the usual port of the chosen mode
            setPort(String(SECURITIES.find((s) => s.value === value)?.port ?? port));
          }}
          className={`${inputClass} sm:max-w-sm`}
        >
          {SECURITIES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={`${id}-username`} className={labelClass}>
            Identifiant
          </label>
          <input
            id={`${id}-username`}
            value={username}
            onChange={(e) => {
              edit(setUsername)(e.target.value);
            }}
            maxLength={255}
            autoComplete="off"
            spellCheck={false}
            className={inputClass}
          />
          <p className={hintClass}>Vide si le serveur n&apos;exige pas d&apos;authentification.</p>
        </div>
        <div>
          <label htmlFor={`${id}-password`} className={labelClass}>
            Mot de passe
          </label>
          <input
            id={`${id}-password`}
            type="password"
            value={password}
            onChange={(e) => {
              edit(setPassword)(e.target.value);
            }}
            maxLength={256}
            disabled={username.trim() === ""}
            // Keeps the browser from filling in the administrator's own Lumen password
            autoComplete="new-password"
            placeholder={keepsPassword ? "••••••••" : undefined}
            className={inputClass}
          />
          <p className={hintClass}>
            {keepsPassword
              ? "Enregistré et chiffré. Laissez vide pour le conserver."
              : settings.has_password && !settings.password_readable
                ? "Le mot de passe enregistré est illisible (clé de l'application modifiée) : saisissez-le à nouveau."
                : "Chiffré à l'enregistrement, jamais réaffiché."}
          </p>
        </div>
      </div>

      <div className="grid gap-4 border-t border-gray-200 pt-6 sm:grid-cols-2 dark:border-gray-700">
        <div>
          <label htmlFor={`${id}-from`} className={labelClass}>
            Adresse d&apos;expédition
          </label>
          <input
            id={`${id}-from`}
            type="email"
            value={fromAddress}
            onChange={(e) => {
              edit(setFromAddress)(e.target.value);
            }}
            required={host.trim() !== ""}
            maxLength={255}
            placeholder={settings.effective_from_address}
            className={inputClass}
          />
          <p className={hintClass}>
            Doit être autorisée par le serveur, sinon les courriels finissent en indésirables.
          </p>
        </div>
        <div>
          <label htmlFor={`${id}-portal`} className={labelClass}>
            Adresse du portail
          </label>
          <input
            id={`${id}-portal`}
            type="url"
            value={portalUrl}
            onChange={(e) => {
              edit(setPortalUrl)(e.target.value);
            }}
            maxLength={500}
            placeholder={settings.effective_portal_url}
            className={inputClass}
          />
          <p className={hintClass}>Utilisée dans les liens envoyés aux lecteurs.</p>
        </div>
      </div>

      <p className="flex items-start gap-2 text-xs text-gray-500 dark:text-gray-400">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        Les champs laissés vides reprennent la configuration du serveur (MAILER_DSN, MAILER_FROM,
        PORTAL_URL).
      </p>

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
        {save.error && (
          <p role="alert" className="mr-auto text-sm text-red-700 dark:text-red-400">
            {save.error.message}
          </p>
        )}
        {saved && !save.error && (
          <p role="status" className="mr-auto text-sm text-emerald-700 dark:text-emerald-400">
            Réglages enregistrés. Envoyez un courriel de test pour les vérifier.
          </p>
        )}
        <Button type="submit" disabled={save.isPending}>
          {save.isPending && <Loader2 className="animate-spin" aria-hidden />}
          Enregistrer
        </Button>
      </div>
    </form>
  );
}
